/**
 * Premium motion primitives. All hooks honour prefers-reduced-motion.
 *
 * Usage from components (data-attribute driven, so JSX stays clean):
 *   <h2 data-word-reveal>...</h2>             → word-by-word clip-mask reveal
 *   <a className="magnetic">...</a>            → cursor-tracking magnetic button
 *   <article className="tilt-card">...         → subtle 3D parallax tilt on hover
 *   <span data-countup data-target="78" />     → number counts from 0 → target
 *   <div data-staggered-bar />                 → score-bar fill triggered on view
 *
 * The single `useMotion` hook wires all DOM-scanning effects once at app root.
 */

import { useEffect } from "react";

const REDUCED = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------------- word-by-word headline reveal ---------------- */
function setupWordReveal(root, signal) {
  const targets = root.querySelectorAll("[data-word-reveal]:not([data-wr-ready])");
  targets.forEach((el) => {
    if (signal.aborted) return;
    const text = el.textContent.trim();
    const nodes = [];
    el.textContent = "";
    // Tokenize preserving spaces and inline accent spans.
    text.split(/(\s+)/).forEach((tok, i) => {
      if (/^\s+$/.test(tok)) {
        el.appendChild(document.createTextNode(" "));
        return;
      }
      const outer = document.createElement("span");
      outer.className = "wr-word";
      const inner = document.createElement("span");
      inner.className = "wr-inner";
      inner.textContent = tok;
      inner.style.transitionDelay = `${Math.min(i, 12) * 60}ms`;
      outer.appendChild(inner);
      el.appendChild(outer);
      nodes.push(outer);
    });
    el.setAttribute("data-wr-ready", "true");
  });

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("wr-active");
          io.unobserve(e.target);
        }
      });
    },
    { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
  );
  targets.forEach((el) => io.observe(el));
  signal.addEventListener("abort", () => io.disconnect());
}

/* ---------------- magnetic buttons ---------------- */
function setupMagnetic(root, signal) {
  const els = root.querySelectorAll(".magnetic:not([data-mag-ready])");
  els.forEach((el) => {
    el.setAttribute("data-mag-ready", "true");
    let raf = 0;
    let tx = 0;
    let ty = 0;
    let cx = 0;
    let cy = 0;

    const onMove = (ev) => {
      const r = el.getBoundingClientRect();
      const mx = ev.clientX - (r.left + r.width / 2);
      const my = ev.clientY - (r.top + r.height / 2);
      // pull at most ~14px from center
      tx = Math.max(-14, Math.min(14, mx * 0.32));
      ty = Math.max(-10, Math.min(10, my * 0.32));
      tick();
    };
    const onLeave = () => {
      tx = 0;
      ty = 0;
      tick();
    };

    const tick = () => {
      cancelAnimationFrame(raf);
      const step = () => {
        cx += (tx - cx) * 0.18;
        cy += (ty - cy) * 0.18;
        el.style.transform = `translate3d(${cx.toFixed(2)}px, ${cy.toFixed(2)}px, 0)`;
        if (Math.abs(tx - cx) > 0.05 || Math.abs(ty - cy) > 0.05) {
          raf = requestAnimationFrame(step);
        }
      };
      raf = requestAnimationFrame(step);
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    signal.addEventListener("abort", () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      el.style.transform = "";
    });
  });
}

/* ---------------- card tilt ---------------- */
function setupTilt(root, signal) {
  const cards = root.querySelectorAll(".tilt-card:not([data-tilt-ready])");
  cards.forEach((el) => {
    el.setAttribute("data-tilt-ready", "true");
    let raf = 0;
    let trx = 0;
    let try_ = 0;
    let crx = 0;
    let cry = 0;
    let hovering = false;

    const onEnter = () => {
      hovering = true;
      el.style.transition = "box-shadow 320ms ease";
      tick();
    };
    const onMove = (ev) => {
      const r = el.getBoundingClientRect();
      const px = (ev.clientX - r.left) / r.width - 0.5; // -.5..+.5
      const py = (ev.clientY - r.top) / r.height - 0.5;
      // max ±4deg keeps it restrained
      try_ = px * 4;
      trx = -py * 4;
      tick();
    };
    const onLeave = () => {
      hovering = false;
      trx = 0;
      try_ = 0;
      tick();
    };
    const tick = () => {
      cancelAnimationFrame(raf);
      const step = () => {
        crx += (trx - crx) * 0.15;
        cry += (try_ - cry) * 0.15;
        el.style.transform = `perspective(900px) rotateX(${crx.toFixed(2)}deg) rotateY(${cry.toFixed(2)}deg)`;
        if (
          hovering ||
          Math.abs(trx - crx) > 0.02 ||
          Math.abs(try_ - cry) > 0.02
        ) {
          raf = requestAnimationFrame(step);
        } else {
          el.style.transform = "";
        }
      };
      raf = requestAnimationFrame(step);
    };

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    signal.addEventListener("abort", () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      el.style.transform = "";
    });
  });
}

/* ---------------- number count-up ---------------- */
function setupCountUp(root, signal) {
  const els = root.querySelectorAll("[data-countup]:not([data-cu-ready])");
  els.forEach((el) => {
    el.setAttribute("data-cu-ready", "true");
    el.textContent = "0";
  });
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        io.unobserve(el);
        const target = parseFloat(el.getAttribute("data-target") || "0");
        const duration = parseInt(el.getAttribute("data-duration") || "1300", 10);
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          // ease-out cubic
          const eased = 1 - Math.pow(1 - t, 3);
          el.textContent = Math.round(target * eased).toString();
          if (t < 1) requestAnimationFrame(step);
          else el.textContent = target.toString();
        };
        requestAnimationFrame(step);
      });
    },
    { threshold: 0.4 }
  );
  els.forEach((el) => io.observe(el));
  signal.addEventListener("abort", () => io.disconnect());
}

/* ---------------- staggered score bars ---------------- */
function setupStaggeredBars(root, signal) {
  const groups = root.querySelectorAll(
    "[data-staggered-bars]:not([data-sb-ready])"
  );
  groups.forEach((g) => g.setAttribute("data-sb-ready", "true"));
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const fills = entry.target.querySelectorAll("[data-bar-fill]");
        fills.forEach((f, i) => {
          const pct = f.getAttribute("data-bar-target") || "0";
          f.style.transition = `width 1100ms cubic-bezier(0.2,0.7,0.2,1) ${i * 90}ms`;
          // force reflow then set width
          // eslint-disable-next-line no-unused-expressions
          f.offsetWidth;
          f.style.width = `${pct}%`;
        });
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.3 }
  );
  groups.forEach((g) => io.observe(g));
  signal.addEventListener("abort", () => io.disconnect());
}

/* ---------------- main hook ---------------- */
export function useMotion() {
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const root = document;

    if (REDUCED()) {
      // Mark targets visible without animation
      root.querySelectorAll("[data-word-reveal]").forEach((el) => {
        el.classList.add("wr-active");
        el.removeAttribute("data-word-reveal");
      });
      root.querySelectorAll("[data-countup]").forEach((el) => {
        el.textContent = el.getAttribute("data-target") || "";
      });
      root.querySelectorAll("[data-staggered-bars] [data-bar-fill]").forEach(
        (f) => {
          f.style.transition = "none";
          f.style.width = `${f.getAttribute("data-bar-target") || 0}%`;
        }
      );
      return () => controller.abort();
    }

    // Defer to next frame so children have mounted.
    requestAnimationFrame(() => {
      setupWordReveal(root, signal);
      setupMagnetic(root, signal);
      setupTilt(root, signal);
      setupCountUp(root, signal);
      setupStaggeredBars(root, signal);
    });

    // Re-scan once after additional renders (audit funnel mounts conditional UI).
    const reScan = () => {
      if (signal.aborted) return;
      setupWordReveal(root, signal);
      setupMagnetic(root, signal);
      setupTilt(root, signal);
      setupCountUp(root, signal);
      setupStaggeredBars(root, signal);
    };
    const interval = setInterval(reScan, 1200);
    signal.addEventListener("abort", () => clearInterval(interval));

    return () => controller.abort();
  }, []);
}

/* ---------------- scrollspy for nav ---------------- */
export function useScrollspy(ids) {
  useEffect(() => {
    if (REDUCED()) return;
    const els = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!els.length) return;

    const setActive = (id) => {
      document
        .querySelectorAll("[data-nav-link]")
        .forEach((a) => {
          if (a.getAttribute("data-nav-target") === id) {
            a.setAttribute("data-active", "true");
          } else {
            a.removeAttribute("data-active");
          }
        });
    };

    const observers = [];
    let currentActive = null;
    const visible = new Set();

    const recompute = () => {
      if (visible.size === 0) return;
      // pick the one closest to top viewport (smallest y >= 0)
      let best = null;
      let bestTop = Infinity;
      visible.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top < bestTop) {
          bestTop = top;
          best = id;
        }
      });
      if (best && best !== currentActive) {
        currentActive = best;
        setActive(best);
      }
    };

    els.forEach((el) => {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) visible.add(el.id);
            else visible.delete(el.id);
          });
          recompute();
        },
        { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
      );
      io.observe(el);
      observers.push(io);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [ids]);
}
